// src/components/MarkLeftDialog.jsx
// App-styled confirmation shown when marking an agent as left/resigned.
// Offers to also (a) return their work number to the free pool and
// (b) return their property cards to the pool (unassign in the property DB).
import { useState, useEffect } from 'react'
import { useDeliberateBackdropDismiss } from '../hooks/useDeliberateBackdropDismiss.js'
import { t } from '../lib/i18n.js'
import { countAgentDrafts } from '../db/index.js'

export function MarkLeftDialog({ name, numberLabel, loginId, onConfirm, onCancel }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onCancel)
  // Default all to on — when an agent leaves you usually free their number/cards
  // AND lock their login (prevents both scheduler + extension sign-in immediately).
  const [removeNumber, setRemoveNumber] = useState(!!numberLabel)
  const [moveCards,    setMoveCards]    = useState(!!loginId)
  const [lockLogin,    setLockLogin]    = useState(!!loginId)
  const [cardCount,    setCardCount]    = useState(null) // null = loading/unknown

  // Show how many cards will move to the pool (read-only count from the property DB).
  useEffect(() => {
    if (!loginId) return
    let live = true
    countAgentDrafts(loginId)
      .then(n => { if (live) setCardCount(n) })
      .catch(() => { if (live) setCardCount(null) })
    return () => { live = false }
  }, [loginId])

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onCancel() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onCancel])

  const checkbox = 'w-4 h-4 rounded border-input accent-primary shrink-0'
  const row = 'flex items-center gap-2.5 cursor-pointer select-none rounded-lg border border-border px-3 py-2.5 hover:bg-accent/40 transition-colors'

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      {...backdropDismiss}>
      <div className="bg-card border border-border rounded-xl w-full max-w-sm shadow-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold">{t('mark_left_title').replace('{name}', name)}</h2>
        </div>

        <div className="px-5 py-4 space-y-2.5 text-sm">
          <p className="text-muted-foreground">{t('mark_left_body')}</p>
          {numberLabel && (
            <label className={row}>
              <input type="checkbox" checked={removeNumber} onChange={e => setRemoveNumber(e.target.checked)} className={checkbox} />
              <span>{t('mark_left_remove_number').replace('{number}', numberLabel)}</span>
            </label>
          )}
          {loginId && (
            <label className={row}>
              <input type="checkbox" checked={moveCards} onChange={e => setMoveCards(e.target.checked)} className={checkbox} />
              <span>
                {t('mark_left_move_cards')}
                <span className="text-muted-foreground"> ({cardCount === null ? '…' : cardCount})</span>
              </span>
            </label>
          )}
          {loginId && (
            <label className={row}>
              <input type="checkbox" checked={lockLogin} onChange={e => setLockLogin(e.target.checked)} className={checkbox} />
              <span>{t('mark_left_lock_login')}</span>
            </label>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onCancel}
            className="text-sm font-medium px-4 py-2 rounded-lg border border-border hover:bg-accent transition-colors">
            {t('cancel')}
          </button>
          <button onClick={() => onConfirm({ removeNumber, moveCards, lockLogin })}
            className="text-sm font-medium px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:opacity-90 transition-opacity">
            {t('qa_fire')}
          </button>
        </div>
      </div>
    </div>
  )
}
